/**
 * PolizzaSalutePage — Pagina informativa pubblica del prodotto
 * "RealmenteInSalute" (Reale Mutua, distribuita da Colucci).
 * SEO ottimizzata, CTA verso /questionario/sanitario.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Heart, Shield, Activity, Sparkles, CheckCircle2, ArrowRight, FileText,
  Stethoscope, Users as UsersIcon, AlertTriangle, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = [
  {
    q: "Da che età posso sottoscrivere RealmenteInSalute?",
    a: "L'età d'ingresso indicativa è 0–60 anni per la variante Vita Intera, con permanenza in copertura fino a 75 anni. Le condizioni complete sono riportate nel DIP/DIP Aggiuntivo.",
  },
  {
    q: "Cosa cambia tra opzione Comfort e Deluxe?",
    a: "L'opzione Comfort prevede massimali standard e accesso al network di strutture convenzionate. La Deluxe ha massimali più elevati, accesso premium e prestazioni accessorie. Il consulente ti illustrerà i dettagli su sub-massimali, franchigie e scoperti.",
  },
  {
    q: "Cos'è la franchigia ricovero?",
    a: "È una quota a tuo carico in caso di ricovero, in cambio di uno sconto sul premio. Permette di ridurre il costo della polizza se ritieni di poter sostenere personalmente eventi di piccola entità.",
  },
  {
    q: "Cos'è lo Sconto Salute?",
    a: "È uno sconto premio per assicurati con stili di vita virtuosi (non fumatore, attività sportiva regolare, parametri biometrici sotto soglia). L'idoneità è verificata in fase di emissione tramite questionario specifico.",
  },
  {
    q: "Devo compilare un questionario sanitario?",
    a: "Sì, ma solo successivamente, in fase di emissione polizza, in contesto controllato dal consulente IVASS. Il modulo web NON raccoglie dati sanitari sensibili (art. 9 GDPR).",
  },
  {
    q: "Posso assicurare anche figli e coniuge?",
    a: "Sì, è prevista la copertura del nucleo familiare in modalità Singolo, Coppia o Famiglia (con figli). Le condizioni e i premi variano in base alla composizione.",
  },
];

export default function PolizzaSalutePage() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rischi-salute/info`).then((r) => setInfo(r.data)).catch(() => {/* fallback hardcoded */});
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="polizza-salute-page">
        {/* HERO */}
        <section className="bg-gradient-to-br from-[#163321] via-[#1d4a2e] to-[#224A32] text-white py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-5 sm:px-6">
            <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-white/70">
              <Heart className="w-4 h-4" /> Polizza Sanitaria — Reale Mutua
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl font-medium tracking-tight mb-4 leading-tight">
              RealmenteInSalute
            </h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl">
              La polizza salute Reale Mutua per proteggere te e la tua famiglia da spese mediche, ricoveri e diagnostica avanzata. Distribuita da <strong>Colucci Assicuratori</strong>.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link
                to="/questionario/sanitario"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-[#163321] font-semibold text-sm hover:bg-white/90 transition-all"
                data-testid="ris-cta-quota"
              >
                Richiedi preventivo <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#dettagli"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-all"
              >
                Scopri il prodotto
              </a>
            </div>
          </div>
        </section>

        <div id="dettagli" className="max-w-4xl mx-auto px-5 sm:px-6 py-12 space-y-10">

          {/* Varianti */}
          <section>
            <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-4">Due varianti, una protezione</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 premium-card">
                <div className="w-12 h-12 rounded-xl bg-[#163321]/10 flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-[#163321]" />
                </div>
                <h3 className="font-heading text-lg text-[#0F1F14] font-medium mb-1">Vita Intera</h3>
                <p className="text-sm text-[#4A5E51] leading-relaxed">Copertura senza scadenza prefissata. Il premio viene rivalutato annualmente in base all'età e alla tariffa. Ideale per protezione di lungo periodo.</p>
              </div>
              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 premium-card">
                <div className="w-12 h-12 rounded-xl bg-[#163321]/10 flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6 text-[#163321]" />
                </div>
                <h3 className="font-heading text-lg text-[#0F1F14] font-medium mb-1">Durata Definita</h3>
                <p className="text-sm text-[#4A5E51] leading-relaxed">Durata pluriennale prefissata (5/10/15/20 anni). Premio bloccato per il periodo, poi rinnovo. Ideale per esigenze a termine.</p>
              </div>
            </div>
          </section>

          {/* Opzioni Comfort/Deluxe */}
          <section>
            <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-4">Comfort vs Deluxe</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border-2 border-[#E5E2DB] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#163321]/10 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#163321]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#0F1F14] font-medium">Comfort</h3>
                </div>
                <ul className="space-y-2 text-sm text-[#4A5E51]">
                  {["Ricovero / Intervento Chirurgico", "Spese pre/post ricovero", "Indennità sostitutiva", "Network di strutture convenzionate"].map((x) => (
                    <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#FFF4EE] to-white border-2 border-[#D97B58] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#D97B58]/15 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#D97B58]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#0F1F14] font-medium">Deluxe</h3>
                  <span className="ml-auto text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-[#D97B58] text-white">TOP</span>
                </div>
                <ul className="space-y-2 text-sm text-[#4A5E51]">
                  {["Tutto ciò che è incluso in Comfort", "Massimali maggiorati", "Accesso premium al network", "Servizi premium e prestazioni accessorie"].map((x) => (
                    <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#D97B58] shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Garanzie extra */}
          <section>
            <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-4">Garanzie extra-ricovero opzionali</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { icon: Activity, title: "Alta Diagnostica", desc: "TAC, RMN, PET, scintigrafie, indagini ad alta specializzazione." },
                { icon: Stethoscope, title: "Diagnostica Completa", desc: "Pacchetto ampio: analisi e diagnostica strumentale." },
                { icon: UsersIcon, title: "Visite Specialistiche", desc: "Visite specialistiche fuori ricovero." },
              ].map((g) => (
                <div key={g.title} className="bg-white border border-[#E5E2DB] rounded-2xl p-4">
                  <div className="w-10 h-10 rounded-xl bg-[#163321]/10 flex items-center justify-center mb-3">
                    <g.icon className="w-5 h-5 text-[#163321]" />
                  </div>
                  <h3 className="font-heading text-base text-[#0F1F14] font-medium mb-1">{g.title}</h3>
                  <p className="text-xs text-[#4A5E51] leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Esempi rimborsi (illustrativi) */}
          <section>
            <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-4">Cosa puoi ottenere (esempi illustrativi)</h2>
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5">
              <ul className="space-y-2 text-sm text-[#4A5E51]">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Rimborso spese mediche per <strong>ricovero con o senza intervento</strong></span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Spese <strong>pre/post ricovero</strong> (accertamenti, riabilitazione)</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span><strong>Indennità sostitutiva</strong> in caso di degenza in struttura pubblica gratuita</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Diagnostica avanzata, visite specialistiche, prestazioni odontoiatriche (a seconda della configurazione scelta)</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Accesso a strutture convenzionate <strong>senza anticipo di spesa</strong> (pagamento diretto)</span></li>
              </ul>
              <p className="text-[11px] text-[#4A5E51] italic mt-4 border-t border-[#E5E2DB] pt-3">Massimali, sub-massimali, franchigie e scoperti variano in base a opzione, garanzie scelte e tariffa applicata. Le condizioni complete sono riportate nel <strong>Set Informativo IVASS</strong> (DIP, DIP Aggiuntivo, Condizioni di Assicurazione).</p>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-4">Domande frequenti</h2>
            <div className="space-y-2">
              {FAQ.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
            </div>
          </section>

          {/* Set informativo IVASS */}
          <section className="bg-[#F5F4F0] border border-[#E5E2DB] rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <FileText className="w-6 h-6 text-[#163321] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-1">Set Informativo IVASS</h2>
                <p className="text-sm text-[#4A5E51] mb-3">Il Set Informativo (DIP, DIP Aggiuntivo, Condizioni di Assicurazione) è disponibile presso il consulente Colucci o sul sito ufficiale Reale Mutua. Per riceverlo via email, indica la richiesta nelle note del modulo di preventivo.</p>
                <a href="https://www.realemutua.it/persone/salute" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#163321] hover:underline">
                  Realemutua.it · Salute <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </section>

          {/* Disclaimer GDPR */}
          <section className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-sm text-blue-900">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h2 className="font-heading text-lg font-medium">Tutela dei dati sanitari (art. 9 GDPR)</h2>
                <p>I dati sanitari del questionario online sono trattati ai sensi dell'<strong>art. 9 c.2 lett. a (consenso esplicito)</strong> e <strong>g (valutazione del rischio assicurativo)</strong> GDPR. La nostra agenzia adotta queste misure di sicurezza:</p>
                <ul className="list-disc pl-5 space-y-1 text-[12px]">
                  <li><strong>Cifratura at-rest AES-256-GCM</strong>: i dati sanitari sono cifrati prima del salvataggio in database con chiave a 256 bit, autenticata (GCM). Il contenuto è tecnicamente illeggibile senza la chiave.</li>
                  <li><strong>TLS in transit</strong>: il transito web è protetto da HTTPS/TLS 1.3.</li>
                  <li><strong>Accesso ristretto</strong>: solo il consulente assicurativo incaricato e il <Link to="/privacy/dpo" className="underline">DPO</Link> possono richiedere la decifratura.</li>
                  <li><strong>Audit log immutabile</strong>: ogni accesso ai dati decifrati viene registrato (chi, quando, motivo) con conservazione 5 anni.</li>
                  <li><strong>Diritto all'oblio oncologico</strong> (L. 193/2023): patologie oncologiche risolte da oltre 10 anni (5 anni se &lt; 21 anni di età) possono NON essere dichiarate.</li>
                  <li><strong>Conservazione</strong>: 5 anni dalla chiusura della richiesta, poi cancellazione automatica.</li>
                  <li><strong>Diritti</strong>: accesso, rettifica, cancellazione, limitazione, opposizione e portabilità (artt. 15-22 GDPR) — esercitabili contattando il DPO.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Disclaimer GDPR sintetico (legacy) */}
          <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-[12px] text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p><strong>Sanitaria è un prodotto lead-only.</strong> Compila il questionario e un nostro consulente Colucci ti contatterà con la <strong>nostra offerta personalizzata</strong> entro 24 ore lavorative. Le polizze sono soggette a <strong>questionario sanitario completo e valutazione del rischio</strong> della compagnia in fase di emissione.</p>
            </div>
          </section>

          {/* CTA finale */}
          <section className="bg-[#163321] text-white rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-medium mb-2">Pronto a proteggere la tua salute?</h2>
            <p className="text-sm sm:text-base text-white/80 mb-5 max-w-xl mx-auto">Compila il modulo di preventivo: ti contattiamo entro 24h lavorative con una proposta personalizzata.</p>
            <Link
              to="/questionario/sanitario"
              className="inline-flex items-center gap-2 px-7 h-12 rounded-full bg-white text-[#163321] font-semibold text-sm hover:bg-white/90 transition-all"
              data-testid="ris-cta-bottom"
            >
              Richiedi preventivo <ArrowRight className="w-4 h-4" />
            </Link>
          </section>

          {info && (
            <p className="text-[10px] text-[#4A5E51] italic text-center">
              {info.disclaimer_ivass}
            </p>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#E5E2DB] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 hover:bg-[#F5F4F0] transition-colors"
      >
        <span className="text-sm font-medium text-[#0F1F14]">{q}</span>
        <ChevronRight className={`w-4 h-4 text-[#4A5E51] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-[#4A5E51] leading-relaxed border-t border-[#E5E2DB] pt-3">
          {a}
        </div>
      )}
    </div>
  );
}
